import Handsontable from "handsontable";
import { IVZHotTableRow, TVZHotTableSummaryFunction } from "./VZHotTable";
import { VZHotTableConfig } from "./vz-hot-table-config";
import { CellMeta, CellProperties } from "handsontable/settings";


export function vzHotTableCells(tableConfig: VZHotTableConfig | undefined) {
    return function cells(this: CellProperties, row: number, col: number): CellMeta {
        this.readOnly = true;
        this.className = this.className ?? "";
        let classNames: Set<string> = new Set();

        if (Array.isArray(this.className)) {
            classNames = new Set(this.className);
        } else {
            classNames = new Set(this.className.split(" "));
        }

        if (tableConfig) {
            const cellConfig = tableConfig.getConfig(row, col);

            if (cellConfig.isReadOnly === false) {
                classNames.add("editable-cell");
                this.readOnly = false;
            }

            if (cellConfig.isSelectable === false) {
                this.disableVisualSelection = true;
            }

            if (typeof cellConfig.format === "function") {
                cellConfig.format(this);
            } else {
                switch (cellConfig.format) {
                    case "percent": {
                        classNames.add("percentage-cell");
                        this.numericFormat = {
                            pattern: "0,0.0",
                            culture: this.numericFormat?.culture ?? "en-US"
                        };
                        this.renderer = (
                            instance,
                            TD,
                            row,
                            col,
                            prop,
                            value,
                            cellProperties
                        ) => {
                            Handsontable.renderers.NumericRenderer(
                                instance,
                                TD,
                                row,
                                col,
                                prop,
                                value,
                                cellProperties
                            );
                        };
                        break;
                    }
                    case "count": {
                        this.numericFormat = {
                            pattern: "0,0",
                            culture: this.numericFormat?.culture ?? "en-US"
                        };
                        this.renderer = (
                            instance,
                            TD,
                            row,
                            col,
                            prop,
                            value,
                            cellProperties
                        ) => {
                            const valueAsNumber = +value;

                            Handsontable.renderers.NumericRenderer(
                                instance,
                                TD,
                                row,
                                col,
                                prop,
                                valueAsNumber,
                                cellProperties
                            );
                        };
                        break;
                    }
                    case "currency":
                        this.numericFormat = {
                            pattern: "$0,0",
                            culture: this.numericFormat?.culture ?? "en-US"
                        };
                        this.renderer = (
                            instance,
                            TD,
                            row,
                            col,
                            prop,
                            value,
                            cellProperties
                        ) => {
                            const numericValue = value == null || value === "" ? null : Math.round(value);

                            Handsontable.renderers.NumericRenderer(
                                instance,
                                TD,
                                row,
                                col,
                                prop,
                                numericValue,
                                cellProperties
                            );
                        };
                        break;
                    case "html":
                        this.renderer = "html";
                        break;
                }
            }

            if (cellConfig.isGreyBackground === true) {
                classNames.add("grey-background");
            }

            if (cellConfig.isRowHeader === true) {
                classNames.add("row-header");
            }

            if (cellConfig.isThickBorderTop === true) {
                classNames.add("thick-border-top");
            }

            if (cellConfig.isThickBorderBottom === true) {
                classNames.add("thick-border-bottom");
            }

            if (cellConfig.isThickBorderRight === true) {
                classNames.add("thick-border-right");
            }

            if (cellConfig.isThickBorderLeft === true) {
                classNames.add("thick-border-left");
            }

            if (cellConfig.isLightGreyBackground === true) {
                classNames.add("light-grey-background");
            }

            if (cellConfig.isBoldText === true) {
                classNames.add("bold-text");
            }

            if (cellConfig.hideBorderBottom === true) {
                classNames.add("hide-border-bottom");
            }

            if (cellConfig.hideBorderRight === true) {
                classNames.add("hide-border-right");
            }

            if (cellConfig.hideText === true) {
                classNames.add("hide-text");
            }
        }

        this.className = Array.from(classNames).join(" ");

        return this;
    };
}

export function getSummaryValue(row: number, col: number, values: number[], summaryFunction: TVZHotTableSummaryFunction): number | null {
    if (!values || !Array.isArray(values)) {
        return 0;
    }

    const valuesAsNumbers = values.map(x => x == null ? 0 : +x);

    if (typeof summaryFunction == "function") {
        return summaryFunction(row, col, valuesAsNumbers);
    } else {
        switch (summaryFunction) {
            case "avg": {
                return +valuesAsNumbers.average().toFixed(2);
            }
            case "sum": {
                return +valuesAsNumbers.sum().toFixed(2);
            }
        }
    }
}

export function defaultSummaryColumnFunction(data: IVZHotTableRow[] | undefined, tableConfig: VZHotTableConfig | undefined, row: Handsontable.RowObject, col = 13): number | null {
    if (!data) {
        return null;
    }

    const rowData: IVZHotTableRow = row as IVZHotTableRow;
    const rowIndex = data.indexOf(rowData);
    const tableSummaryFunction = tableConfig?.tableSummaryFunction ?? "sum";
    const summaryFunction = rowData.rowSummaryFunction ?? tableSummaryFunction;
    const summaryValue = getSummaryValue(rowIndex, col, rowData.values, summaryFunction);

    return summaryValue;
}
